import React, { useEffect, useMemo, useState } from 'react';
import { makeAwsSignedRequest } from '../services/ApiService';
import { EmptyState, ErrorIndicator, LoadingIndicator } from './common/LoadingStates';
import './BacklogBurndownPanel.scss';

interface BurndownPoint {
    timestamp: string;
    overSla: number;
}

interface Props {
    stage: string;
    realm: string;
    startDateTime?: Date;
    endDateTime?: Date;
    trackerData: {
        timeWindow?: string;
        clientId?: string;
        metadata?: { generatedAt?: string; [key: string]: any };
    } | null;
}

const parseNumber = (v: unknown): number => {
    const n = typeof v === 'number' ? v : Number(v);
    return Number.isFinite(n) ? n : 0;
};

// Reuse the /tracker/trends series (overSla per interval) to show the stuck-backlog burn-down.
const toSeries = (raw: any): BurndownPoint[] => {
    const series = raw?.series ?? raw?.data?.series ?? [];
    if (!Array.isArray(series)) {
        return [];
    }
    return series
        .filter((p: any) => p && typeof p.timestamp === 'string')
        .map((p: any) => ({ timestamp: p.timestamp, overSla: parseNumber(p.overSla) }));
};

const BacklogBurndownPanel: React.FC<Props> = ({ stage, realm, trackerData, startDateTime, endDateTime }) => {
    const [series, setSeries] = useState<BurndownPoint[] | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!trackerData?.timeWindow || !trackerData?.clientId) {
            setSeries(null);
            setError(null);
            return;
        }
        let active = true;
        const controller = new AbortController();

        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const params = new URLSearchParams({
                    timeWindow: trackerData.timeWindow || 'now-30d',
                    clientId: trackerData.clientId || 'AllClients',
                });
                if (startDateTime) {
                    params.set('startTime', startDateTime.toISOString());
                }
                if (endDateTime) {
                    params.set('endTime', endDateTime.toISOString());
                }
                const data = await makeAwsSignedRequest(stage, realm, `tracker/trends?${params.toString()}`, {
                    signal: controller.signal,
                    timeoutMs: 120000,
                });
                if (active) {
                    setSeries(toSeries(data));
                }
            } catch (err) {
                if (!active || controller.signal.aborted) {
                    return;
                }
                setSeries(null);
                setError(err instanceof Error ? err.message : 'Failed to load backlog burn-down');
            } finally {
                if (active) {
                    setLoading(false);
                }
            }
        };

        load();
        return () => {
            active = false;
            controller.abort();
        };
    }, [stage, realm, trackerData?.timeWindow, trackerData?.clientId, trackerData?.metadata?.generatedAt,
        startDateTime?.getTime(), endDateTime?.getTime()]);

    const stats = useMemo(() => {
        if (!series || series.length === 0) {
            return null;
        }
        const start = series[0].overSla;
        const current = series[series.length - 1].overSla;
        const peak = series.reduce((m, p) => Math.max(m, p.overSla), 0);
        const net = current - start;
        return { start, current, peak, net };
    }, [series]);

    // SVG sparkline of the over-SLA backlog across the selected range.
    const sparkline = useMemo(() => {
        if (!series || series.length < 2) {
            return null;
        }
        const width = 640;
        const height = 120;
        const pad = 8;
        const max = Math.max(1, ...series.map((p) => p.overSla));
        const dx = (width - 2 * pad) / (series.length - 1);
        const points = series.map((p, i) => {
            const x = pad + i * dx;
            const y = height - pad - (p.overSla / max) * (height - 2 * pad);
            return `${x.toFixed(1)},${y.toFixed(1)}`;
        });
        return { width, height, polyline: points.join(' '), area: `${pad},${height - pad} ${points.join(' ')} ${(width - pad).toFixed(1)},${height - pad}` };
    }, [series]);

    if (!trackerData) {
        return null;
    }

    const trend = stats ? (stats.net < 0 ? 'improving' : stats.net > 0 ? 'worsening' : 'flat') : 'flat';
    const trendColor = trend === 'improving' ? '#0f766e' : trend === 'worsening' ? '#a63a50' : '#64748b';

    return (
        <section className="backlog-burndown-panel">
            <div className="backlog-burndown-panel__header">
                <div>
                    <p className="eyebrow">Over-SLA backlog</p>
                    <h2>Stuck backlog burn-down</h2>
                    <p className="subtitle">Net change in the over-SLA refund count across the selected range.</p>
                </div>
                {stats && (
                    <div className="backlog-burndown-panel__net" style={{ color: trendColor }}>
                        {stats.net > 0 ? '+' : ''}{stats.net.toLocaleString()}
                        <span className="backlog-burndown-panel__net-label">
                            {trend === 'improving' ? 'burning down' : trend === 'worsening' ? 'growing' : 'no change'}
                        </span>
                    </div>
                )}
            </div>

            {loading && <LoadingIndicator />}
            {!loading && error && <ErrorIndicator error={error} />}
            {!loading && !error && (!series || series.length === 0) && (
                <EmptyState title="No backlog data" message="No backlog data for the selected range." />
            )}

            {!loading && !error && stats && (
                <>
                    <div className="backlog-burndown-panel__stats">
                        <div><span className="label">Start</span><span className="value">{stats.start.toLocaleString()}</span></div>
                        <div><span className="label">Current</span><span className="value">{stats.current.toLocaleString()}</span></div>
                        <div><span className="label">Peak</span><span className="value">{stats.peak.toLocaleString()}</span></div>
                    </div>
                    {sparkline && (
                        <svg
                            className="backlog-burndown-panel__spark"
                            viewBox={`0 0 ${sparkline.width} ${sparkline.height}`}
                            preserveAspectRatio="none"
                            role="img"
                            aria-label="Over-SLA backlog over time"
                        >
                            <polygon points={sparkline.area} fill={trendColor} fillOpacity="0.12" />
                            <polyline points={sparkline.polyline} fill="none" stroke={trendColor} strokeWidth="2" />
                        </svg>
                    )}
                </>
            )}
        </section>
    );
};

export default BacklogBurndownPanel;
